import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useInView({
  threshold = 0.15,
  rootMargin = '0px 0px -8% 0px',
  triggerOnce = true,
} = {}) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (prefersReducedMotion()) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (triggerOnce) observer.disconnect();
        } else if (!triggerOnce) {
          setIsInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isInView];
}

export function useRevealOnMount() {
  const [isReady, setIsReady] = useState(prefersReducedMotion());

  useEffect(() => {
    if (prefersReducedMotion()) return;

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsReady(true));
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  return isReady;
}
